import {
  norwegianLicensePlateCarsAndTrucksRegex,
  norwegianLicensePlateOtherVehiclesRegex,
  partialNorwegianLicensePlateRegex,
  swedishLicensePlateRegex
} from '../regex/regex';
import { CountryCode, CountryCodeType } from '../utils/constants';

export const isValidLicensePlateNumber = ({
  input,
  countryCode
}: {
  input: string;
  countryCode: CountryCodeType;
}): boolean => {
  if (countryCode === 'NO') {
    return (
      input === '' ||
      partialNorwegianLicensePlateRegex.test(input) ||
      norwegianLicensePlateCarsAndTrucksRegex.test(input) ||
      norwegianLicensePlateOtherVehiclesRegex.test(input)
    );
  }
  return input === '' || swedishLicensePlateRegex.test(input);
};

/** Lengths a complete plate has: AB12345 / AB1234 in Norway, ABC123 in Sweden. */
export const getAcceptedLicensePlateNumberLengths = ({ countryCode }: { countryCode: CountryCodeType }): number[] => {
  if (countryCode === CountryCode.NORWAY) {
    return [6, 7];
  } else if (countryCode === CountryCode.SWEDEN) {
    return [6];
  }

  throw new Error(`Unsupported country code: ${countryCode}`);
};

/** A full plate, ready to look up: valid characters at a complete length. */
export const isCompleteLicensePlateNumber = ({
  input,
  countryCode
}: {
  input: string;
  countryCode: CountryCodeType;
}): boolean =>
  input !== '' &&
  isValidLicensePlateNumber({ input, countryCode }) &&
  getAcceptedLicensePlateNumberLengths({ countryCode }).includes(input.length);

/**
 * Splits pasted text into plate numbers as stored by the backend: whitespace and dashes removed, in upper case.
 * Repeats are dropped from `plates` and listed once in `duplicates`.
 */
export type ParsedLicensePlates = { plates: string[]; duplicates: string[] };

export const parseLicensePlates = (text: string): ParsedLicensePlates => {
  const plates: string[] = [];
  const duplicates = new Set<string>();
  for (const raw of text.split(/[\n,;\t]+/)) {
    const plate = normalizeLicensePlate(raw);
    if (!plate) {
      continue;
    }
    if (plates.includes(plate)) {
      duplicates.add(plate);
    } else {
      plates.push(plate);
    }
  }
  return { plates, duplicates: [...duplicates] };
};

/** Plate as the backend stores and indexes it: whitespace and dashes removed, upper case. */
export const normalizeLicensePlate = (input: string) => input.replace(/[\s-]/g, '').toUpperCase();

/**
 * A complete plate someone can look a car up by — "BD97155", "bd 97155" and "BD-97155" all qualify.
 * Plate-shaped means the search can target plate-bearing indexes only; a partial plate cannot.
 */
export const isLicensePlateLookup = (input: string) => {
  const plate = normalizeLicensePlate(input);
  return (
    isCompleteLicensePlateNumber({ input: plate, countryCode: CountryCode.NORWAY }) ||
    isCompleteLicensePlateNumber({ input: plate, countryCode: CountryCode.SWEDEN })
  );
};

// Keep registration numbers contiguous: inserting a space prevents browser Find from matching the full plate.
export const formatLicensePlate = (plate: string) =>
  plate.toUpperCase().replace(/\b([A-Z]{2,3})\s+(?=\d{2,5}[A-Z]?\b)/g, '$1');

// Paper is never searched, so a printed plate reads as the physical plate does: "EP 23935".
export const formatPrintedLicensePlate = (plate: string) =>
  formatLicensePlate(plate).replace(/\b([A-Z]{2,3})(?=\d{2,5}[A-Z]?\b)/g, '$1 ');
